import React, { useMemo, useState } from 'react';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { dateTypeMap, getPlaceholderText } from '../../constants/placeholder';
import type { FilterKeys } from '../../types';
import './index.scss';
import { formatDateFunc } from '@/pages/opinion/utils/time';
import { getLocale } from 'umi';
import { logicTextMap } from '../../constants/regionAndLanguage';
import type { LocaleType } from '@/pages/opinion/types/common';
import useTranslate from '../../hooks/useTranslate';
import { defaultFilter } from '../../constants/filterOptions';
import { has } from 'lodash-es';
import { NO_DATE } from '@op/constants';

type RenderTitleType = (
  key: FilterKeys,
  element: string,
) => {
  otherInfo: string;
  element: string;
};

export default function FilterFixedHeader({
  change,
  filterName,
  filter = defaultFilter,
  renderFixedHeader,
  lang,
  showFilterText,
  isAccountRegion = false,
  renderExcludeFilter = [],
}: {
  change: () => void;
  filterName?: string;
  filter?: Array<FilterKeys>;
  renderFixedHeader: { [key: string]: any };
  lang: LocaleType;
  showFilterText: boolean;
  isAccountRegion?: boolean;
  renderExcludeFilter?: Array<FilterKeys>;
}) {
  const { formatMessage, translateInfo, needTranslateElement } = useTranslate();

  const renderFixedHeaderMap: {
    [key in FilterKeys]?: (key: FilterKeys) => JSX.Element | null | Element;
  } = {
    regionAndLanguage: (key: string) => {
      const [regionTitle, languageTitle] = ['region', 'language'].map((key) =>
        getPlaceholderText(key as FilterKeys, lang),
      );

      return (
        <div className="filterbox-left-flex" key={key}>
          <span className="filterbox-left-describe">{regionTitle}</span>
          <span className="filterbox-county">
            {needTranslateElement(renderFixedHeader?.region)}
          </span>
          <span className="filterbox-and">
            {logicTextMap[renderFixedHeader?.logic as 'and' | 'or']?.[lang]}
          </span>
          <span className="filterbox-left-describe">{languageTitle}</span>
          <span className="filterbox-left-text">
            {renderFixedHeader?.language}
          </span>
        </div>
      );
    },
    date: (key: string) => {
      const { start_time, end_time, date_type } = renderFixedHeader?.date || {};
      const formatDate = formatDateFunc(date_type);
      return (
        <div className="filterbox-left-flex" key={key}>
          <span className="filterbox-left-describe">
            {dateTypeMap?.[date_type]?.[getLocale()]}
          </span>
          <span className="filterbox-left-text">
            {formatDate(start_time)} <span className="text-grey">～</span>
            {formatDate(end_time)}
          </span>
        </div>
      );
    },
    liveVenues: () => null,
    keyKol: () => null,
  };

  const renderFixedHeaderTitle: { [key in FilterKeys]?: RenderTitleType } & {
    common: RenderTitleType;
  } = {
    common: (_key: string, element: string) => {
      return { otherInfo: '', element };
    },
    steamIds: () => {
      const isExclude = has(renderFixedHeader, 'exclude_steam_ids');
      const newKey = isExclude ? 'exclude_steam_ids' : 'steam_ids';
      const otherInfo = `(${formatMessage({
        id: isExclude ? 'Exclude' : 'Include',
      })})`;

      return {
        otherInfo,
        element: translateInfo(renderFixedHeader?.[newKey]),
      };
    },
    topic: (_key: string, element: string) => {
      const { topic_rule } = renderFixedHeader;
      return {
        otherInfo: `(${formatMessage({
          id: topic_rule || NO_DATE.EMPTY,
        })})`,
        element,
      };
    },
    monitor: (_key: string, element: string) => {
      const { monitor_source_match_mode } = renderFixedHeader;
      return {
        otherInfo: `(${formatMessage({
          id: monitor_source_match_mode || NO_DATE.EMPTY,
        })})`,
        element,
      };
    },
    keyWords: (_key: string, element: string) => {
      const { keywordListMode = 'include' } = renderFixedHeader;
      return {
        otherInfo: `(${formatMessage({
          id: keywordListMode === 'exclude' ? 'Exclude' : 'Include',
        })})`,
        element,
      };
    },
  };

  const filterShowElements = useMemo(() => {
    return filter
      .filter((item) => !renderExcludeFilter.includes(item))
      .map((key: FilterKeys) => {
        const renderFunc = renderFixedHeaderMap[key];
        if (renderFunc) return renderFunc(key);

        const titleKey: any =
          isAccountRegion && key === 'region' ? 'accountRegion' : key;

        const title = getPlaceholderText(titleKey, lang);
        const oriElement = translateInfo(renderFixedHeader?.[key]);
        const renderTextFunc =
          renderFixedHeaderTitle[key] || renderFixedHeaderTitle.common;
        const { otherInfo, element } = renderTextFunc(key, oriElement);

        return (
          <div className="filterbox-left-flex" key={key}>
            <span className="filterbox-left-describe">
              {title}
              {otherInfo}
            </span>
            <span className="filterbox-left-text">{element}</span>
          </div>
        );
      });
  }, [renderFixedHeader, lang, formatMessage]);

  const [showNull, setShowNull] = useState(false);

  function onClickChange() {
    setShowNull(true);
    change();

    setTimeout(() => {
      setShowNull(false);
    }, 300);
  }

  return (
    <div
      className="dbo-filterfixed-header-wrapper dbo-filterbox-header-flex"
      onClick={onClickChange}
    >
      <div className="filterbox-left">
        {showFilterText && (
          <div className="filterbox-left-flex">
            <SvgIcon type="funnel" className="filterbox-left-describe" />
            <span className="filterbox-left-text">
              {filterName || formatMessage({ id: 'My Filter' })}
            </span>
          </div>
        )}
        {!showNull && (filterShowElements as any)}
      </div>
      <div className="filterbox-right">
        <span>{formatMessage({ id: 'Show' })}</span>
        <SvgIcon type="point-down" className="filterbox-right-icon" />
      </div>
    </div>
  );
}
